// Web Speech API wrapper. Falls back gracefully when TTS is unavailable.
//
// Picks a warm female "teacher" voice from the browser's synth voice list
// (priority order below). We override utterance.voice on every speak() call
// so the picked voice is used instead of the OS default (which tends to be a
// robotic male voice on macOS/Windows/Linux Chromium).

const SUPPORTED = typeof window !== "undefined" && "speechSynthesis" in window;

// Priority list of preferred female-teacher voice NAMES (case-insensitive
// substring match). Order matters — first match wins.
const PREFERRED_VOICE_NAMES = [
  "Samantha", "Karen", "Moira", "Tessa", "Susan", "Serena", "Fiona",
  "Ava", "Allison", "Nicky",
  "Google US English", "Microsoft Zira", "Microsoft Aria",
  "Google UK English Female", "Kate", "Emma", "Amelie",
];

// Secondary keyword fallback (case-insensitive substring in name).
const FEMALE_KEYWORDS = ["female", "woman", "girl"];

// Names that clearly denote a male voice — reject even if lang matches.
const MALE_REJECT_NAMES = [
  "male", "man", "daniel", "alex", "fred", "ralph", "bruce", "junior",
];

let _pickedVoice = null;

function pickFemaleVoice() {
  if (!SUPPORTED) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;

  const isMale = (name) => {
    const n = name.toLowerCase();
    return MALE_REJECT_NAMES.some((m) => n.includes(m));
  };
  const langOk = (v) => /^en(-|_|$)/i.test(v.lang || "");
  const enVoices = voices.filter((v) => langOk(v) && !isMale(v.name));

  for (const wanted of PREFERRED_VOICE_NAMES) {
    const hit = enVoices.find((v) =>
      v.name.toLowerCase().includes(wanted.toLowerCase()),
    );
    if (hit) return hit;
  }
  for (const kw of FEMALE_KEYWORDS) {
    const hit = enVoices.find((v) => v.name.toLowerCase().includes(kw));
    if (hit) return hit;
  }
  return enVoices[0] || voices.find((v) => !isMale(v.name)) || voices[0];
}

function refreshVoice() {
  const v = pickFemaleVoice();
  if (v && v !== _pickedVoice) {
    _pickedVoice = v;
    // Log once so the user can confirm which voice was picked on their system.
    console.log("[speech] selected voice:", v.name, v.lang);
  }
}

if (SUPPORTED) {
  refreshVoice();
  // Voice list loads asynchronously in Chromium — re-pick when it lands.
  window.speechSynthesis.addEventListener?.("voiceschanged", refreshVoice);
}

// Fixed teacher-warmth prosody applied to every utterance. See task spec.
const TEACHER_PITCH = 1.05;
const TEACHER_RATE = 0.85;

function letterPause(speed) {
  if (speed === "slow") return 250;
  if (speed === "fast") return 100;
  return 150;
}

// Fixed ribbon cadence used exclusively for the spellingAudioMode="off"
// branch. Keeps the visual letter-by-letter reveal predictable so a
// silent-mode child still sees each letter land in rhythm.
const SPELLING_MODE_OFF_CADENCE_MS = 250;

// speakLetters fast-mode multiplier — both the utterance rate AND the
// letter-callback timers scale by this so the ribbon stays in sync with
// the audio. Anything much above 1.5 makes single-letter TTS sound clipped.
const SPELLING_FAST_MULTIPLIER = 1.4;

let currentUtterance = null;
let currentLetterTimers = [];
let listeners = new Set();

function emit(event) {
  listeners.forEach((cb) => {
    try {
      cb(event);
    } catch (_) {
      /* ignore listener errors */
    }
  });
}

export const speech = {
  supported: SUPPORTED,

  on(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },

  cancel() {
    if (SUPPORTED) {
      try {
        window.speechSynthesis.cancel();
      } catch (_e) { /* ignore */ }
    }
    currentLetterTimers.forEach((t) => clearTimeout(t));
    currentLetterTimers = [];
    currentUtterance = null;
    emit({ type: "cancel" });
  },

  isSpeaking() {
    return SUPPORTED ? window.speechSynthesis.speaking : false;
  },

  // Debug helper — returns the currently-picked voice so callers/consoles
  // can verify what was selected on this system.
  getSelectedVoice() {
    return _pickedVoice
      ? { name: _pickedVoice.name, lang: _pickedVoice.lang, default: _pickedVoice.default }
      : null;
  },

  async speak(text, { lang = "en-US" } = {}) {
    if (!text) return;
    this.cancel();
    if (!SUPPORTED) {
      emit({ type: "start", text });
      // Estimate reading time so callers awaiting completion still get a
      // sensible pause.
      const ms = Math.min(4000, Math.max(600, text.length * 60));
      await new Promise((r) => setTimeout(r, ms));
      emit({ type: "end", text });
      return;
    }
    // Late voice pick — some browsers only populate after first user gesture.
    if (!_pickedVoice) refreshVoice();
    return new Promise((resolve) => {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = TEACHER_RATE;
      u.pitch = TEACHER_PITCH;
      u.lang = lang;
      if (_pickedVoice) u.voice = _pickedVoice;
      u.onstart = () => emit({ type: "start", text });
      u.onend = () => {
        emit({ type: "end", text });
        resolve();
      };
      u.onerror = () => {
        emit({ type: "error", text });
        resolve();
      };
      currentUtterance = u;
      window.speechSynthesis.speak(u);
    });
  },

  async speakLetters(letters, { speed = "normal", lang = "en-US", onLetter, spellingMode = "slow" } = {}) {
    if (!letters || !letters.length) return;
    this.cancel();
    if (!_pickedVoice) refreshVoice();

    // spellingAudioMode="off" — no MP3, just fire the letter callbacks on a
    // fixed cadence so the ribbon still lights up in rhythm. Word audio and
    // celebration/hint audio are handled by ``speak()`` and are unaffected.
    if (spellingMode === "off") {
      for (let i = 0; i < letters.length; i++) {
        const raw = letters[i];
        if (onLetter) onLetter({ index: i, letter: raw });
        emit({ type: "letterStart", index: i, letter: raw });
        await new Promise((r) => {
          const wait = raw === " " ? SPELLING_MODE_OFF_CADENCE_MS * 2 : SPELLING_MODE_OFF_CADENCE_MS;
          const timer = setTimeout(r, wait);
          currentLetterTimers.push(timer);
        });
      }
      emit({ type: "spellingComplete" });
      return;
    }

    const fast = spellingMode === "fast";
    // In "fast" mode we accelerate BOTH the utterance rate and the between-
    // letter pause so the ribbon stays in sync with the shortened audio.
    const rate = fast ? TEACHER_RATE * SPELLING_FAST_MULTIPLIER : TEACHER_RATE;
    const gap = fast ? Math.round(letterPause(speed) / SPELLING_FAST_MULTIPLIER) : letterPause(speed);
    for (let i = 0; i < letters.length; i++) {
      const raw = letters[i];
      if (onLetter) onLetter({ index: i, letter: raw });
      emit({ type: "letterStart", index: i, letter: raw });
      const isSpace = raw === " ";
      if (!isSpace) {
        await new Promise((resolve) => {
          if (!SUPPORTED) {
            setTimeout(resolve, 260 - Math.round(gap / 2));
            return;
          }
          const u = new SpeechSynthesisUtterance(raw);
          u.rate = rate;
          u.pitch = TEACHER_PITCH;
          u.lang = lang;
          if (_pickedVoice) u.voice = _pickedVoice;
          u.onend = resolve;
          u.onerror = resolve;
          window.speechSynthesis.speak(u);
        });
      }
      // Pause between letters (longer for space)
      await new Promise((r) => {
        const timer = setTimeout(r, isSpace ? gap * 2 : gap);
        currentLetterTimers.push(timer);
      });
    }
    emit({ type: "spellingComplete" });
  },
};
